/* AgenticEnvironmentGraph — « un monde que NOAM comprend ».
 * Représentation connectée RÉELLE de l'environnement (graph/overview existant,
 * zéro relation inventée). Bidirectionnel : un clic sur un élément permet
 * d'en parler à NOAM (instrument → agent) ou d'ouvrir l'instrument. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlow, Background, MarkerType, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader2, MessageCircle, ExternalLink, Network, Maximize2, X } from "lucide-react";
import { api } from "@/lib/api";
import { TYPE_STYLE, ROUTE_BY_TYPE, layout } from "@/pages/saas/GraphExplorer";

function MiniNode({ data }) {
  const s = TYPE_STYLE[data.type] || TYPE_STYLE.signal;
  const isEntity = data.type === "entity";
  const emphasized = data.selected || data.highlighted;
  return (
    <div data-testid={`agentic-env-graph-node-${data.type || "signal"}`} style={{
      background: s.bg, border: `1.5px solid ${data.selected ? "#fff" : s.color}`,
      borderRadius: isEntity ? 999 : 7, padding: isEntity ? "7px 12px" : "4px 8px",
      maxWidth: 160, cursor: "pointer",
      boxShadow: emphasized ? `0 0 16px ${s.color}88` : "none",
      animation: data.highlighted && !data.selected ? "noamNodePulse 2.2s ease-in-out infinite" : "none",
    }}>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <div style={{ fontSize: 7, textTransform: "uppercase", letterSpacing: 1, color: s.color }}>{s.label}</div>
      <div style={{ fontSize: isEntity ? 10.5 : 9, fontWeight: isEntity ? 700 : 500, color: "#f4f4f5", lineHeight: 1.2 }}>
        {data.label}
      </div>
    </div>
  );
}
const nodeTypes = { mini: MiniNode };

export default function AgenticEnvironmentGraph({ heightClass = "h-[400px]", highlightTypes = [], subtitle = null, onTalk = null }) {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/workspace/graph/overview", { params: { limit: 24 } })
      .then((r) => {
        const allNodes = r.data?.nodes || [];
        const allEdges = r.data?.edges || [];
        // Représentation CONNECTÉE : on ne montre que les éléments réellement reliés.
        const linked = new Set();
        allEdges.forEach((e) => { linked.add(e.source); linked.add(e.target); });
        const connected = allNodes.filter((n) => linked.has(n.id));
        setNodes(connected.length > 0 ? connected : allNodes.slice(0, 12));
        setEdges(allEdges);
        setStats(r.data?.stats || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const { flowNodes, flowEdges } = useMemo(() => {
    const pos = layout(nodes, edges);
    const hl = new Set(highlightTypes);
    return {
      flowNodes: nodes.map((n) => ({
        id: n.id, type: "mini", position: pos[n.id] || { x: 0, y: 0 },
        data: { ...n, selected: selected?.id === n.id, highlighted: hl.has(n.type) },
      })),
      flowEdges: edges.map((e, i) => ({
        id: `e${i}`, source: e.source, target: e.target,
        style: { stroke: "#52525b", strokeWidth: Math.max(1, (e.weight || 0.5) * 2), opacity: 0.75 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#52525b", width: 10, height: 10 },
      })),
    };
  }, [nodes, edges, selected, highlightTypes]);

  const talkAbout = () => {
    if (!selected) return;
    api.post("/workspace/agentic/track", { event: "instrument_to_agent", meta: { surface: "agentic_home_graph", node_type: selected.type } }).catch(() => {});
    const s = TYPE_STYLE[selected.type] || {};
    const intent = `Parle-moi de « ${selected.label} » (${s.label || selected.type}) : ce que tu observes, ses relations dans mon environnement, et ce qui mérite mon attention.`;
    if (onTalk) { onTalk(intent); setSelected(null); return; }
    navigate(`/app/intelligence?intent=${encodeURIComponent(intent)}`);
  };

  const openInstrument = () => {
    if (!selected) return;
    api.post("/workspace/agentic/track", { event: "agent_to_instrument", meta: { surface: "agentic_home_graph", node_type: selected.type } }).catch(() => {});
    if (selected.type === "entity" && selected.meta?.entity_id) navigate(`/app/annuaire?entity=${selected.meta.entity_id}`);
    else navigate(ROUTE_BY_TYPE[selected.type] || "/app/graph");
  };

  const relationCount = edges.length;

  return (
    <div className="noam-card p-0 overflow-hidden flex flex-col" data-testid="agentic-env-graph">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#EFD127] font-semibold flex items-center gap-1.5">
            <Network className="w-3 h-3" /> Votre environnement connecté
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {subtitle || (relationCount > 0
              ? `${relationCount} relation${relationCount > 1 ? "s" : ""} réelle${relationCount > 1 ? "s" : ""} connue${relationCount > 1 ? "s" : ""} de NOAM. Cliquez sur un élément pour en parler.`
              : "NOAM relie les éléments de votre organisation entre eux.")}
          </p>
        </div>
        <button onClick={() => navigate("/app/graph")} data-testid="agentic-env-graph-explore"
          className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors shrink-0">
          Explorer <Maximize2 className="w-3 h-3" />
        </button>
      </div>

      <div className={`relative ${heightClass}`}>
        <style>{`@keyframes noamNodePulse { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.45); } }`}</style>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Lecture de l'environnement…
          </div>
        ) : nodes.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 text-sm gap-2 px-8 text-center" data-testid="agentic-env-graph-empty">
            <Network className="w-8 h-8 opacity-30" />
            <p>NOAM n'a encore aucune relation à représenter dans votre environnement.</p>
            <p className="text-xs text-zinc-600">Ajoutez des entités, documents ou dossiers : les relations réelles apparaîtront ici, jamais des relations inventées.</p>
          </div>
        ) : (
          <ReactFlow
            nodes={flowNodes} edges={flowEdges} nodeTypes={nodeTypes}
            onNodeClick={(_, n) => setSelected(selected?.id === n.id ? null : n.data)}
            onPaneClick={() => setSelected(null)}
            fitView fitViewOptions={{ padding: 0.08, maxZoom: 1.1 }} minZoom={0.05} proOptions={{ hideAttribution: true }}
            style={{ background: "transparent" }}
            nodesDraggable={false} nodesConnectable={false} elementsSelectable={false}>
            <Background color="#1f1f23" gap={26} size={1} />
          </ReactFlow>
        )}

        {selected && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 rounded-xl border border-white/15 bg-[#0b0f18]/95 backdrop-blur px-3 py-2.5 shadow-xl shadow-black/40"
            data-testid="agentic-env-graph-node-actions">
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-widest" style={{ color: (TYPE_STYLE[selected.type] || {}).color }}>
                {(TYPE_STYLE[selected.type] || {}).label || selected.type}
              </div>
              <div className="text-xs text-white truncate">{selected.label}</div>
            </div>
            <button onClick={talkAbout} data-testid="agentic-env-graph-talk"
              className="btn-primary text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shrink-0">
              <MessageCircle className="w-3.5 h-3.5" /> Parler à NOAM de cet élément
            </button>
            <button onClick={openInstrument} data-testid="agentic-env-graph-open"
              className="btn-secondary text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shrink-0">
              <ExternalLink className="w-3.5 h-3.5" /> Instrument
            </button>
            <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-white p-1 shrink-0" data-testid="agentic-env-graph-close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {stats && nodes.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap px-4 py-2.5 border-t border-white/5 text-[9px]" data-testid="agentic-env-graph-stats">
          {Object.entries(TYPE_STYLE).map(([k, s]) => {
            const count = k === "entity" ? stats.entities : k === "risk" ? stats.risks
              : k === "mandate" ? stats.mandates : k === "chain" ? stats.chains
              : k === "opp" ? stats.opps : k === "signal" ? stats.signals : null;
            if (!count) return null;
            return (
              <span key={k} className="px-1.5 py-0.5 rounded-full border" style={{ borderColor: `${s.color}45`, color: s.color, background: `${s.color}0c` }}>
                {s.label} · {count}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
